import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { FeatureFlagProvider } from './hooks/useFeatureFlag';
import { ThemeProvider } from './hooks/useTheme';
import { Toaster } from 'sonner';
import MaintenanceWrapper from './components/MaintenanceWrapper';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
// Critical page loaded eagerly (landing page — first paint)
import Home from './pages/Home';
import NotFoundPage from './pages/NotFoundPage';
// All other pages lazy-loaded for better Core Web Vitals (LCP / FID)
const SearchResults = lazy(() => import('./pages/SearchResults'));
const ListingDetail = lazy(() => import('./pages/ListingDetail'));
const BusinessesPage = lazy(() => import('./pages/BusinessesPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const ClassifiedsPage = lazy(() => import('./pages/ClassifiedsPage'));
const TownPage = lazy(() => import('./pages/TownPage'));
const TownsPage = lazy(() => import('./pages/TownsPage'));
const MicroNichePage = lazy(() => import('./pages/MicroNichePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const BlogAuthorPage = lazy(() => import('./pages/BlogAuthorPage'));
const ReviewsOfPage = lazy(() => import('./pages/ReviewsOfPage'));
const ComparePage = lazy(() => import('./pages/ComparePage'));
const RequestsPage = lazy(() => import('./pages/RequestsPage'));
const RequestDetail = lazy(() => import('./pages/RequestDetail'));
const JoinPage = lazy(() => import('./pages/JoinPage'));
const ListYourBusinessPage = lazy(() => import('./pages/ListYourBusinessPage'));
const GetListedLanding = lazy(() => import('./pages/GetListedLanding'));
const WhyJoinPage = lazy(() => import('./pages/WhyJoinPage'));
const BestOfPage = lazy(() => import('./pages/BestOfPage'));
const BestOfIndex = lazy(() => import('./pages/BestOfPage').then(m => ({ default: m.BestOfIndex })));
const TownGuidePage = lazy(() => import('./pages/TownGuidePage'));
const ReferPage = lazy(() => import('./pages/ReferPage'));
const ClaimListingPage = lazy(() => import('./pages/ClaimListingPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const CommunityPostPage = lazy(() => import('./pages/CommunityPostPage'));
const EmbedWidgetPage = lazy(() => import('./pages/EmbedWidgetPage'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
// Lazy-loaded pages (admin, forms, utility)
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminSetup = lazy(() => import('./pages/AdminSetup'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminUserProfile = lazy(() => import('./pages/admin/AdminUserProfile'));
const CreateListing = lazy(() => import('./pages/CreateListing'));
const EditListing = lazy(() => import('./pages/EditListing'));
const ListingSubmitted = lazy(() => import('./pages/ListingSubmitted'));
const Pricing = lazy(() => import('./pages/Pricing'));
const PromoteCategoryPage = lazy(() => import('./pages/PromoteCategoryPage'));
// SEO landing-page suite (hub + spokes + programmatic)
const FreeAdvertisingEssex = lazy(() => import('./pages/FreeAdvertisingEssex'));
const FreeBusinessListing = lazy(() => import('./pages/FreeBusinessListing'));
const PostFreeClassified = lazy(() => import('./pages/PostFreeClassified'));
const PromoteEvent = lazy(() => import('./pages/PromoteEvent'));
const FindTradesman = lazy(() => import('./pages/FindTradesman'));
const TradesByTown = lazy(() => import('./pages/TradesByTown'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const MFASetup = lazy(() => import('./pages/MFASetup'));
const MFAVerify = lazy(() => import('./pages/MFAVerify'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const HelpSupport = lazy(() => import('./pages/HelpSupport'));
const AdvertisePage = lazy(() => import('./pages/AdvertisePage'));
const AdvertiseCategoryPage = lazy(() => import('./pages/AdvertiseCategoryPage'));
const Messages = lazy(() => import('./pages/Messages'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const NewsletterConfirm = lazy(() => import('./pages/NewsletterConfirm'));
const NewsletterUnsubscribe = lazy(() => import('./pages/NewsletterUnsubscribe'));
const MarketingUnsubscribe = lazy(() => import('./pages/MarketingUnsubscribe'));
const OutreachUnsubscribe = lazy(() => import('./pages/OutreachUnsubscribe'));
const OutreachSourceDisclosure = lazy(() => import('./pages/OutreachSourceDisclosure'));
const CreateRequest = lazy(() => import('./pages/CreateRequest'));
const ClaimBusinessPage = lazy(() => import('./pages/ClaimBusinessPage'));
const CancelFeedbackPage = lazy(() => import('./pages/CancelFeedbackPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const HowWeVerifyPage = lazy(() => import('./pages/HowWeVerifyPage'));
const HowWeRankPage = lazy(() => import('./pages/HowWeRankPage'));
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import InstallPrompt from './components/InstallPrompt';
import UpdateNotification from './components/UpdateNotification';
import ChatbotWidget from './components/ChatbotWidget';
import BackToTop from './components/BackToTop';
import BadgeEarnedPopup from './components/BadgeEarnedPopup';
import './App.css';

const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-pulse space-y-4 w-full max-w-md px-4">
      <div className="h-8 bg-slate-200 rounded w-3/4 mx-auto" />
      <div className="h-4 bg-slate-200 rounded w-1/2 mx-auto" />
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
      <AuthProvider>
        <FeatureFlagProvider>
        <MaintenanceWrapper>
          <BrowserRouter>
          <ScrollToTop />
          <Layout>
            <ErrorBoundary>
            <Suspense fallback={<LazyFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/join" element={<JoinPage />} />
              <Route path="/list-your-business" element={<ListYourBusinessPage />} />
              <Route path="/get-listed" element={<GetListedLanding />} />
              <Route path="/why-join" element={<WhyJoinPage />} />
              <Route path="/search" element={<SearchResults />} />
              {/* Dedicated category pages with SEO-friendly URLs */}
              <Route path="/business" element={<Navigate to="/businesses" replace />} />
              <Route path="/businesses" element={<BusinessesPage />} />
              <Route path="/business/:categorySlug" element={<BusinessesPage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/marketplace" element={<ClassifiedsPage />} />
              <Route path="/classified-ads" element={<Navigate to="/marketplace" replace />} />
              <Route path="/listing/:townSlug/:categorySlug/:titleSlug" element={<ListingDetail />} />
              <Route path="/listing/:id" element={<ListingDetail />} />
              <Route path="/town/:townSlug" element={<TownPage />} />
              <Route path="/towns" element={<TownsPage />} />
              <Route path="/category/:category" element={<Navigate to="/businesses" replace />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/author/:slug" element={<BlogAuthorPage />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/reviews-of/:slug" element={<ReviewsOfPage />} />
              <Route path="/compare/:slug" element={<ComparePage />} />
              <Route path="/best-of-essex" element={<BestOfIndex />} />
              <Route path="/best-of-essex/:slug" element={<BestOfPage />} />
              <Route path="/claim/:leadId" element={<ClaimListingPage />} />
              <Route path="/growth-listing/:leadId" element={<ClaimListingPage />} />
              <Route path="/best/:guideSlug" element={<TownGuidePage />} />
              <Route path="/refer" element={<ReferPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/how-we-verify" element={<HowWeVerifyPage />} />
              <Route path="/editorial-standards" element={<HowWeVerifyPage />} />
              <Route path="/how-we-rank" element={<HowWeRankPage />} />
              <Route path="/ranking-methodology" element={<HowWeRankPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/post/:postId" element={<CommunityPostPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/mfa-setup" element={<MFASetup />} />
              <Route path="/mfa-verify" element={<MFAVerify />} />
              <Route path="/admin-setup" element={<AdminSetup />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/embed-widget" element={<EmbedWidgetPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/user/:userId" element={<AdminUserProfile />} />
              <Route path="/create-listing" element={<CreateListing />} />
              <Route path="/edit-listing/:id" element={<EditListing />} />
              <Route path="/listing-submitted" element={<ListingSubmitted />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/promote/:categorySlug" element={<PromoteCategoryPage />} />
              {/* SEO landing pages — free-advertising hub + spokes */}
              <Route path="/free-advertising-essex" element={<FreeAdvertisingEssex />} />
              <Route path="/free-business-listing-essex" element={<FreeBusinessListing />} />
              <Route path="/post-free-classified-essex" element={<PostFreeClassified />} />
              <Route path="/promote-event-essex" element={<PromoteEvent />} />
              <Route path="/find-tradesman-essex" element={<FindTradesman />} />
              {/* Programmatic SEO — every (trade × town) combo gets its own page */}
              <Route path="/trades/:trade/:town" element={<TradesByTown />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/advertise" element={<AdvertisePage />} />
              <Route path="/advertise/:categorySlug" element={<AdvertiseCategoryPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/help" element={<HelpSupport />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/messages/:conversationId" element={<Messages />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
              <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />
              <Route path="/email/unsubscribe" element={<MarketingUnsubscribe />} />
              <Route path="/unsubscribe-outreach/:leadId" element={<OutreachUnsubscribe />} />
              <Route path="/outreach-source/:leadId" element={<OutreachSourceDisclosure />} />
              {/* Find a Trade (formerly Requests) */}
              <Route path="/find-a-trade" element={<RequestsPage />} />
              <Route path="/requests" element={<Navigate to="/find-a-trade" replace />} />

              {/* SEO Landing Pages - P2 backlog */}
              <Route path="/request/new" element={<Navigate to="/create-listing?type=service_request" replace />} />
              <Route path="/request/:requestId" element={<RequestDetail />} />
              {/* 404 Catch-all route - must be last */}
              <Route path="/services/:categorySlug/:townSlug" element={<MicroNichePage />} />
              <Route path="/claim-business" element={<ClaimBusinessPage />} />
              <Route path="/cancel-feedback" element={<CancelFeedbackPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </Layout>
        <ChatbotWidget />
        <BackToTop />
        </BrowserRouter>
        <BadgeEarnedPopup />
        <Toaster position="top-right" />
        <OfflineBanner />
        <InstallPrompt />
        <UpdateNotification />
        </MaintenanceWrapper>
        </FeatureFlagProvider>
      </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;